import * as React from 'react';
import { EVENTS } from 'analytics/analytics-events';
import mixpanel from 'mixpanel-browser';
import { ACTIVITY_BUFFER, MIN_ZOOM_RANGE_MS } from '../support';

import { useActivityEditor } from './use-activity-editor';
import { usePrevious } from 'shared';
import { GraphTool, GraphToolEnum, GraphType, GraphTypeEnum } from './types';
import HighchartsReact from 'highcharts-react-official';

export interface ChartContextShape {
    chartRef: React.RefObject<HighchartsReact.RefObject>;
    graphRange: [number, number];
    chartZoom: [number, number];
    setChartZoom: React.Dispatch<React.SetStateAction<[number, number]>>;
    resetZoom: () => void;
    graphedDeviceIds: number[];
    setGraphedDeviceIds: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    setGraphTool: React.Dispatch<React.SetStateAction<GraphToolEnum>>;
    graphTool: GraphToolEnum;
    setGraphType: React.Dispatch<React.SetStateAction<GraphTypeEnum>>;
    graphType: GraphTypeEnum;
}

const noProviderError = () => {
    console.error('No ChartContextProvider is present');
};

const ChartContext = React.createContext<ChartContextShape>({
    chartRef: React.createRef<HighchartsReact.RefObject>(),
    graphRange: [0, 0],
    chartZoom: [0, 0],
    setChartZoom: noProviderError,
    resetZoom: noProviderError,
    graphedDeviceIds: [],
    setGraphedDeviceIds: noProviderError,
    setGraphTool: noProviderError,
    graphTool: GraphTool.zoom,
    setGraphType: noProviderError,
    graphType: GraphType.individual,
});

export const ChartContextProvider: React.FunctionComponent = ({ children }) => {
    //Creating reference to chart
    const chartRef = React.useRef<HighchartsReact.RefObject>(null);
    const { activity, athletePanelCards, periods } = useActivityEditor();
    const [chartZoom, actualSetChartZoom] = React.useState<[number, number]>([0, 0]);
    const [graphedDeviceIds, setGraphedDeviceIds] = React.useState<number[]>();
    const [graphTool, setGraphTool] = React.useState<GraphToolEnum>(GraphTool.zoom);
    const [graphType, setGraphType] = React.useState<GraphTypeEnum>(GraphType.individual);

    const graphRange = React.useMemo((): [number, number] => {
        /**
         * On first load the periods are empty, so we need to return a range of 0,0.
         * Calculate the graph range on empty periods will cause an Infinity range and break the UI
         */
        if (periods.length) {
            const lowestValue = Math.min(...periods.map((period) => period.startDateTime.getTime()));
            const highestValue = Math.max(...periods.map((period) => period.endDateTime.getTime()));
            const buffer = Math.ceil((highestValue - lowestValue) * ACTIVITY_BUFFER);
            return [lowestValue - buffer, highestValue + buffer];
        }
        return [0, 0];
    }, [periods]);

    const setChartZoom: React.Dispatch<React.SetStateAction<[number, number]>> = React.useCallback(
        (newChartZoom) => {
            let myChartZoom: [number, number];
            if (typeof newChartZoom === 'function') {
                myChartZoom = newChartZoom(chartZoom);
            } else {
                myChartZoom = [...newChartZoom];
            }

            // Constrain zoom to a minimum range of 10 seconds and to be inside the graph range

            // If outside to the left, adjust to the right
            if (myChartZoom[0] < graphRange[0]) {
                myChartZoom[1] += graphRange[0] - myChartZoom[0];
                myChartZoom[0] = graphRange[0];
            }

            // If outside to the right, adjust to the left
            if (myChartZoom[1] > graphRange[1]) {
                myChartZoom[0] -= myChartZoom[1] - graphRange[1];
                myChartZoom[1] = graphRange[1];
            }

            let currentRange = myChartZoom[1] - myChartZoom[0];

            // If zoom is too small, move the limits to the min range
            if (currentRange < MIN_ZOOM_RANGE_MS) {
                const adjustBy = (MIN_ZOOM_RANGE_MS - currentRange) / 2;
                myChartZoom[1] += adjustBy;
                myChartZoom[0] -= adjustBy;
            }

            // Ensure we're still inside the graph range
            myChartZoom[0] = Math.max(myChartZoom[0], graphRange[0]);
            myChartZoom[1] = Math.min(myChartZoom[1], graphRange[1]);

            currentRange = myChartZoom[1] - myChartZoom[0];

            // If zoom is too small, move the limits to the min range
            if (currentRange < MIN_ZOOM_RANGE_MS) {
                const adjustBy = (MIN_ZOOM_RANGE_MS - currentRange) / 2;
                myChartZoom[1] += adjustBy;
                myChartZoom[0] -= adjustBy;
            }

            actualSetChartZoom(myChartZoom);
        },
        [chartZoom, graphRange],
    );

    const resetZoom = React.useCallback(() => {
        if (activity) {
            actualSetChartZoom(graphRange);
            mixpanel.track(EVENTS.editing.zoomReset);
        }
    }, [activity, graphRange, actualSetChartZoom]);

    // Reset when switching activities
    const prevActivityId = usePrevious(activity?.id);
    React.useEffect(() => {
        if (prevActivityId !== activity?.id) {
            setGraphedDeviceIds(undefined);
        }
    }, [prevActivityId, activity?.id]);

    return (
        <ChartContext.Provider
            value={{
                chartRef,
                chartZoom,
                setChartZoom,
                resetZoom,
                graphRange,
                graphedDeviceIds:
                    graphedDeviceIds || (athletePanelCards?.[0]?.deviceId ? [athletePanelCards[0].deviceId] : []),
                setGraphedDeviceIds,
                setGraphTool,
                graphTool,
                setGraphType,
                graphType,
            }}
        >
            {children}
        </ChartContext.Provider>
    );
};

export const useChartContext = () => React.useContext(ChartContext);
